import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { TestModuleDialog } from './dialogs/test-module-dialog';
import { Menu } from 'primereact/menu';
import { restoreIframeValues, setIframeAnswerFieldsReadOnly } from "./content-viewer/rendering-helpers";
import { attachPreviewLinkHandler } from "./content-viewer/preview-links";
import { useApi } from "../providers/api-provider";
import { applyPreviewWatermark } from "./content-viewer/preview-watermark";
import { CARD_GROUP_STYLES } from '../general/card-group-layout';
import { prepareResponsiveTables, TABLE_RESPONSIVE_STYLES } from '../general/table-responsive';
import { COLUMNS_STYLES } from '../general/columns-layout';
import { LESSON_CONTENT_STYLES } from '../general/lesson-content-styles';
import { useInputWordCompletion } from './word-completion-toggle';
import { themeChangedEvent } from '../general/forum-theme';

export interface HtmlViewerHandle {
    validate: () => Record<string, string[]> | null;
    getValues: () => Record<string, any> | null;
    resetValues: () => void;
}

type HtmlViewerProps = {
  htmlContent: string;
  theme: string;
  instructions: string;
  onMessage?: (kind: string, data: any) => void;
  onHtmlChanged?: (data: string) => Promise<void>;
  onInstructionsChanged?: (data: string) => Promise<void>;
  selectedMode: "text" | "preview" | "instructions";
  readOnly?: boolean;
  hideControls?: boolean;
  compactControls?: boolean;
  sandboxStyles: boolean;
  inputValues?: Record<string, any>;
  onValuesReady?: (values: Record<string, any>) => void;
  inputsReadOnly?: boolean;
  onPreviewReady?: (frame: HTMLIFrameElement) => void;
  previewTitle?: string;
};

export const HtmlViewer = forwardRef<HtmlViewerHandle, HtmlViewerProps>( 
function HtmlViewer(
    { htmlContent, theme, onMessage, selectedMode, readOnly, onHtmlChanged, hideControls, compactControls = false, instructions, onInstructionsChanged, sandboxStyles, inputValues, onValuesReady, inputsReadOnly = false, onPreviewReady, previewTitle = 'HTML viewer' }: HtmlViewerProps,
    ref) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const api = useApi();
    const apiRef = useRef(api);
    apiRef.current = api;
    const detachPreviewLinks = useRef<(() => void) | null>(null);
    const menuRef = useRef<Menu>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [srcDoc, setSrcDoc] = useState<string>("");
    const [viewMode, setViewMode] = useState<"text" | "preview" | "instructions">(selectedMode ?? "preview");
    const [testVisible, setTestVisible] = useState<boolean>(false);
    const [completionDocument, setCompletionDocument] = useState<Document | null>(null);
    useInputWordCompletion(viewMode === 'preview' ? completionDocument : null, inputsReadOnly);

    useEffect(() => {
        setIframeAnswerFieldsReadOnly(iframeRef.current, inputsReadOnly);
    }, [inputsReadOnly]);

    useEffect(() => () => {
        detachPreviewLinks.current?.();
        detachPreviewLinks.current = null;
    }, [viewMode]);

    useImperativeHandle(ref, () => ({
      validate() {
        const win = iframeRef.current?.contentWindow as any;
        if (!win || typeof win.validateForm !== "function") return null;
        return win.validateForm();
      },

      resetValues() {
        const win = iframeRef.current?.contentWindow as any;
        if (!win || typeof win.resetForm !== "function") return null;
        return win.resetForm();
      },

      getValues() {
        const win = iframeRef.current?.contentWindow as any;
        if (!win || typeof win.getFormValues !== "function") return null;
        return win.getFormValues();
      }
    }));

    function buildIsolatedHtml(htmlContent: string): string {
        const isFullDocument: boolean = htmlContent.includes("<html");
        if (isFullDocument) {
            const reset = `<style>html, body { all: initial; }</style>`;
            const patched = htmlContent.replace("<head>", `<head>${reset}`);
            return patched;
        } else {
            return `<!DOCTYPE html>
    <html>
        <head><style>html, body { all: initial; }</style></head>
        <body>
        ${htmlContent}
        </body>
    </html>
    `;
        }
    }

    function insertTextAtCursor(text: string) {
        const textarea: HTMLTextAreaElement | null = textAreaRef.current;
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;

            const before = textarea.value.substring(0, start);
            const after = textarea.value.substring(end);

            textarea.value = before + text + after;

            // Move cursor to end of inserted text
            const newPos = start + text.length;
            textarea.selectionStart = textarea.selectionEnd = newPos;

            // Keep focus
            textarea.focus();
        }
    }

    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe) {
            const fullHtml: string = sandboxStyles ? buildIsolatedHtml(htmlContent) : htmlContent;
            // collapse collapsible panels by default in preview mode
            const collapsedHtml: string = (fullHtml ?? '').replace(
                /<div class="collapsible"/g,
                '<div class="collapsible collapsed"'
            );

            setSrcDoc(collapsedHtml);
        }
    }, [htmlContent, viewMode, selectedMode]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
        if (
            event.data?.type === 'custom-event' ||
            event.data?.type === 'graph-node-clicked' ||
            event.data?.type === 'ai-selection-explain'
        ) {
            onMessage?.(event.data?.type, event.data.payload);
        }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onMessage]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || sandboxStyles) return;

        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        const updateTheme = () => {
            const effectiveTheme = (theme ?? '').length > 0 ? theme : localStorage.getItem('selected-theme');
            let themeLink = doc.getElementById('themelink') as HTMLLinkElement;
            
            if (!themeLink) {
                themeLink = doc.createElement('link');
                themeLink.id = 'themelink';
                themeLink.rel = 'stylesheet';
                doc.head.appendChild(themeLink);
            }

            const newHref = effectiveTheme ? `/themes/${effectiveTheme}/theme.css` : '';
            
            // Force update even if the browser thinks it's the same
            if (themeLink.href !== newHref) {
                themeLink.href = newHref;
            }
        };

        // If the iframe is already loaded, update now.
        // If not, it will be handled by the onLoad handler.
        if (doc.readyState === 'complete') {
            updateTheme();
        }
        window.addEventListener(themeChangedEvent, updateTheme);
        return () => window.removeEventListener(themeChangedEvent, updateTheme);
    }, [theme, sandboxStyles]);

    const codeSnippetsItems = [
        { label: "Insert post text snippet", icon: "pi pi-code",
            command: () => insertTextAtCursor(`window.parent.postMessage({ type: 'custom-event', payload: 'my text' }, '*');\n`) },
        { label: "Insert post object snippet", icon: "pi pi-code",
            command: () => insertTextAtCursor(`const payload = { prop1: 'string value' };\npayload.prop2 = 123;\nwindow.parent.postMessage({ type: 'custom-event', payload: payload }, '*');\n`) },
        { label: "Insert post text snippet surrounded by <script>", icon: "pi pi-code",
            command: () => insertTextAtCursor(`<script language='javascript'>\n  window.parent.postMessage({ type: 'custom-event', payload: 'my text' }, '*');\n</script>\n`) },
        { label: "Insert post object snippet surrounded by <script>", icon: "pi pi-code",
            command: () => insertTextAtCursor(`<script language='javascript'>\n  const payload = { prop1: 'string value' };\n  payload.prop2 = 123;\n  window.parent.postMessage({ type: 'custom-event', payload: payload }, '*');\n</script>\n`) },
    ];

    function attachCollapsibleListener(doc: Document): void {
        doc.addEventListener("click", (e: MouseEvent) => {
            const target = e.target as HTMLElement | null
            if (!target) return

            const header = target.closest(".collapsible-header")
            if (!header) return

            const parent = header.parentElement
            if (!parent) return

            parent.classList.toggle("collapsed")
        });
    }

    const iframeLoaded = async (e: any): Promise<void> => {
        const doc = e.currentTarget.contentDocument;
        detachPreviewLinks.current?.();
        detachPreviewLinks.current = doc ? attachPreviewLinkHandler(doc, document, (url, signal) => {
            if (!apiRef.current) return Promise.reject(new Error('The API is not ready.'));
            return apiRef.current.downloadFile(url, signal);
        }) : null;
        if (!doc) return;
        setCompletionDocument(doc);
        if (!doc.getElementById('card-group-styles')) {
            const style = doc.createElement('style');
            style.id = 'card-group-styles';
            style.textContent = CARD_GROUP_STYLES + TABLE_RESPONSIVE_STYLES + COLUMNS_STYLES + LESSON_CONTENT_STYLES;
            doc.head.appendChild(style);
        }
        applyPreviewWatermark(doc);
        prepareResponsiveTables(doc);
        if (!sandboxStyles) {
            try {
                const effectiveTheme = (theme ?? '').length > 0 ? theme : localStorage.getItem('selected-theme');
                let themeLink = doc.getElementById('themelink') as HTMLLinkElement;
                if (!themeLink) {
                    themeLink = doc.createElement('link');
                    themeLink.id = 'themelink';
                    themeLink.rel = 'stylesheet';
                    doc.head.appendChild(themeLink);
                }
                const newHref: string = effectiveTheme ? `/themes/${effectiveTheme}/theme.css` : '';
                if (themeLink && themeLink.getAttribute('href') !== newHref) {
                    themeLink.href = newHref;
                }

                const primeflexLink = document.querySelector('link[href*="primeflex"]');
                if (primeflexLink) {
                    doc.head.appendChild(primeflexLink.cloneNode(true));
                }
                // PrimeFlex utilities do not include lesson component styles.
                const url = "/assets/editor-utilities.css";
                if (!doc.querySelector(`link[href="${url}"]`)) {
                    const link = doc.createElement("link");
                    link.rel = "stylesheet";
                    link.href = url;
                    doc.head.appendChild(link);
                }
            } catch (err) {
                console.log(err);
            }
        }

        const contextMenuScript = doc.createElement("script");
        contextMenuScript.textContent = `document.addEventListener('contextmenu', e => e.preventDefault());`;
        doc.body.appendChild(contextMenuScript);

        attachCollapsibleListener(doc);

        restoreIframeValues(iframeRef.current, inputValues ?? {});
        setIframeAnswerFieldsReadOnly(iframeRef.current, inputsReadOnly);
        const win = iframeRef.current?.contentWindow as any;
        if (typeof win?.getFormValues === 'function') onValuesReady?.(win.getFormValues());
        if (srcDoc) onPreviewReady?.(e.currentTarget);
    }

    return (<>
        {!(hideControls ?? false) && (
            <div className={`html-viewer-controls${compactControls ? ' admin-toolbar' : ' flex gap-2 mb-3'}`}>
                <Button
                    label="Preview"
                    icon="pi pi-eye"
                    className={viewMode === "preview" ? "p-button-primary" : "p-button-outlined"}
                    onClick={() => setViewMode("preview")}
                    style={{ width: "auto" }}
                />
                <Button
                    label="Source text"
                    icon="pi pi-code"
                    className={viewMode === "text" ? "p-button-primary" : "p-button-outlined"}
                    onClick={() => setViewMode("text")}
                    style={{ width: "auto" }}
                />
                <Button
                    label="Instructions"
                    icon="pi pi-code"
                    className={viewMode === "instructions" ? "p-button-primary" : "p-button-outlined"}
                    onClick={() => setViewMode("instructions")}
                    style={{ width: "auto" }}
                />
                <Button
                    label="Test"
                    icon="pi pi-play"
                    className={"p-button-outlined"}
                    onClick={() => { setTestVisible(true); }}
                    style={{ width: "auto" }}
                />

                <Menu model={codeSnippetsItems} popup ref={menuRef} />
                <Button
                    title="Actions"
                    icon="pi pi-chevron-down"
                    visible={!readOnly}
                    onClick={(e) => menuRef.current?.toggle(e)}
                />
            </div>
        )}
        {viewMode === "text" && (
            <InputTextarea
                ref={textAreaRef}
                value={htmlContent ?? ""}
                onChange={(e) => onHtmlChanged?.(e.target.value ?? '')}
                readOnly={readOnly ?? true}
                style={{ width: '100%', height: '100%', border: 'none' }}
            />
        )}
        {viewMode === "preview" && (
            <iframe
                ref={iframeRef}
                title={previewTitle}
                style={{ width: '100%', height: '100%', border: 'none' }}
                sandbox="allow-scripts allow-same-origin"
                srcDoc={srcDoc}
                onLoad={iframeLoaded}
            />
        )}
        {viewMode === "instructions" && (
            <InputTextarea
                value={instructions ?? ""}
                onChange={(e) => onInstructionsChanged?.(e.target.value ?? '')}
                readOnly={readOnly ?? true}
                style={{ width: '100%', height: '100%', border: 'none' }}
            />
        )}

        <TestModuleDialog 
            htmlContent={`${srcDoc}<script>document.addEventListener('contextmenu', e => e.preventDefault());</script>`}
            theme={theme}
            instructions={instructions}
            onClose={() => { setTestVisible(false); }}
            visible={testVisible}
        />
    </>);
    }
)
